import { useState, useEffect, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { useUgcDraft } from '../../../models/ugc-draft/context';
import { usePushToastOrFlashMessage } from '../../../components/v2/toaster/deprecation-helper';
import useScreenTitle from '../../../common/use-screen-title';

type UGCDraftFormProps = {
  children: ({ onSubmit }: { onSubmit: () => void }) => ReactNode;
};

const UGCDraftForm = ({ children }: UGCDraftFormProps) => {
  const [navigateAway, setNavigateAway] = useState(false);
  const { ugcDraft, upsertUgcDraft, hasRequiredInput } = useUgcDraft();
  useScreenTitle(ugcDraft.title || 'New Post');
  const { pushMessage } = usePushToastOrFlashMessage();
  const { t } = useTranslation();
  const history = useHistory();

  const handleSubmitSuccess = (messageType: 'created' | 'updated') => {
    setNavigateAway(true);

    pushMessage({
      text: t(`ugc_draft.${messageType}`),
      type: 'success',
    });
  };

  const handleSubmitFailure = (err: Error) => {
    console.error('UGC draft submission error', err);

    pushMessage({
      text: t('errors.default'),
      type: 'error',
    });
  };

  const handleSubmit = () => {
    upsertUgcDraft({
      onSuccess: () => handleSubmitSuccess(ugcDraft.id ? 'updated' : 'created'),
      onError: handleSubmitFailure,
    });
  };

  const handleDiscardChanges = () => {
    //TODO: (FE-26994) If there are unsaved changes to the form, there should be a warning to the user (will be addressed in a separate ticket)
    return true;
  };

  useEffect(() => {
    if (navigateAway) history.goBack();
  }, [navigateAway, history]);

  return (
    <>
      <Prompt
        message={handleDiscardChanges}
        when={hasRequiredInput && !navigateAway}
      />

      <form
        aria-label={t('ugc_draft.form_label')}
        onSubmit={(e) => e.preventDefault()}
      >
        {children({ onSubmit: handleSubmit })}
      </form>
    </>
  );
};

export default UGCDraftForm;
